import React, { useState, useRef } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Divider,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
  PersonRemove as PersonRemoveIcon,
  GetApp as ExportIcon,
  FileCopy as CloneIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Group as GroupIcon
} from '@mui/icons-material';
import { Task, PEOPLE, RAPID_ROLES } from '../types';
import { exportToExcel, exportToCSV, exportToPDF, exportToJSON, ExportData } from '../utils/exportUtils';

interface DecisionMatrixProps {
  tasks: Task[];
  onTasksChange: (tasks: Task[]) => void;
  projectName: string;
  showSnackbar: (message: string, severity?: 'success' | 'error' | 'info') => void;
  peopleConfig?: {
    people: any[];
    personNames: { [key: string]: string };
  };
  onPeopleConfigChange?: (people: any[], personNames: { [key: string]: string }) => void;
}

const DecisionMatrix: React.FC<DecisionMatrixProps> = ({ tasks, onTasksChange, projectName, showSnackbar, peopleConfig, onPeopleConfigChange }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCell, setSelectedCell] = useState<{ taskId: string; personId: string } | null>(null);
  const [personNames, setPersonNames] = useState<{ [key: string]: string }>(peopleConfig?.personNames || {});
  const [people, setPeople] = useState(peopleConfig?.people || PEOPLE);
  const [exportDialog, setExportDialog] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const matrixRef = useRef<HTMLDivElement>(null);
  const [addPersonDialog, setAddPersonDialog] = useState(false);
  const [newPersonName, setNewPersonName] = useState('');
  const [newPersonCategory, setNewPersonCategory] = useState<'Leadership Team' | 'Project Team' | 'External Resources'>('Project Team');

  const handleAddTask = () => {
    const newTask: Task = {
      id: `task-${Date.now()}`,
      workArea: '',
      taskDescription: '',
      assignments: {}
    };
    onTasksChange([...tasks, newTask]);
  };

  const handleDeleteTask = (taskId: string) => {
    onTasksChange(tasks.filter(task => task.id !== taskId));
  };

  const handleTaskChange = (taskId: string, field: 'workArea' | 'taskDescription', value: string) => {
    onTasksChange(tasks.map(task => 
      task.id === taskId 
        ? { ...task, [field]: value }
        : task
    ));
  };

  const handleRoleAssignment = (taskId: string, personId: string, role: string) => {
    onTasksChange(tasks.map(task => {
      if (task.id === taskId) {
        const newAssignments = { ...task.assignments };
        if (role === '') {
          delete newAssignments[personId];
        } else {
          newAssignments[personId] = role;
        }
        return { ...task, assignments: newAssignments };
      }
      return task;
    }));
    setAnchorEl(null);
    setSelectedCell(null);
  };

  const getRoleColor = (role: string) => {
    const rapidRole = RAPID_ROLES.find(r => r.id === role);
    return rapidRole ? rapidRole.color : '#ffffff';
  };

  const handleCellClick = (event: React.MouseEvent<HTMLElement>, taskId: string, personId: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedCell({ taskId, personId });
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedCell(null);
  };

  const handlePersonNameChange = (personId: string, name: string) => {
    const newPersonNames = {
      ...personNames,
      [personId]: name
    };
    setPersonNames(newPersonNames);
    if (onPeopleConfigChange) {
      onPeopleConfigChange(people, newPersonNames);
    }
  };

  const handleTogglePersonVisibility = (personId: string) => {
    const newPeople = people.map(p => 
      p.id === personId 
        ? { ...p, isVisible: !p.isVisible }
        : p
    );
    setPeople(newPeople);
    if (onPeopleConfigChange) {
      onPeopleConfigChange(newPeople, personNames);
    }
    showSnackbar(
      `Person ${personId} ${people.find(p => p.id === personId)?.isVisible ? 'hidden' : 'shown'}`, 
      'success'
    );
  };

  const handleToggleCategoryVisibility = (category: string, show: boolean) => {
    const newPeople = people.map(p => 
      p.category === category 
        ? { ...p, isVisible: show }
        : p
    );
    setPeople(newPeople);
    if (onPeopleConfigChange) {
      onPeopleConfigChange(newPeople, personNames);
    }
    showSnackbar(
      `${category} members ${show ? 'shown' : 'hidden'}`, 
      'success'
    );
  };

  const handleShowAllPeople = () => {
    const newPeople = people.map(p => ({ ...p, isVisible: true }));
    setPeople(newPeople);
    if (onPeopleConfigChange) {
      onPeopleConfigChange(newPeople, personNames);
    }
    showSnackbar('All team members shown', 'success');
  };

  const handleAddPerson = () => {
    if (!newPersonName.trim()) {
      showSnackbar('Please enter a person name', 'error');
      return;
    }

    // Generate new person ID
    const existingIds = people.map(p => {
      const match = p.id.match(/P(\d+)/);
      return match ? parseInt(match[1]) : 0;
    });
    const maxId = Math.max(...existingIds, 0);
    const newPersonId = `P${maxId + 1}`;

    // Create new person
    const newPerson = {
      id: newPersonId,
      name: newPersonName,
      category: newPersonCategory,
      isVisible: true
    };

    // Find the correct position to insert the new person
    // Group order: Leadership Team -> Project Team -> External Resources
    const categoryOrder = ['Leadership Team', 'Project Team', 'External Resources'];
    const newCategoryIndex = categoryOrder.indexOf(newPersonCategory);

    // Create a copy of people array and find where to insert
    const updatedPeople = [...people];
    let insertIndex = updatedPeople.length; // Default to end

    // Find the last person in the same category or the first person in the next category
    for (let i = 0; i < updatedPeople.length; i++) {
      const currentCategoryIndex = categoryOrder.indexOf(updatedPeople[i].category);

      if (currentCategoryIndex > newCategoryIndex) {
        // Found a person in a later category, insert before them
        insertIndex = i;
        break;
      } else if (updatedPeople[i].category === newPersonCategory) {
        // Found someone in the same category, keep looking for the last one
        insertIndex = i + 1;
      }
    }

    // Insert the new person at the correct position
    updatedPeople.splice(insertIndex, 0, newPerson);

    const updatedPersonNames = {
      ...personNames,
      [newPersonId]: newPersonName
    };

    setPeople(updatedPeople);
    setPersonNames(updatedPersonNames);

    if (onPeopleConfigChange) {
      onPeopleConfigChange(updatedPeople, updatedPersonNames);
    }

    showSnackbar(`Added ${newPersonName} to ${newPersonCategory}`, 'success');
    setAddPersonDialog(false);
    setNewPersonName('');
    setNewPersonCategory('Project Team');
  };

  const handleRemovePerson = (personId: string) => {
    const personToRemove = people.find(p => p.id === personId);
    if (!personToRemove) return;

    // Check if this is the only person in their category
    const peopleInSameCategory = people.filter(p => p.category === personToRemove.category);

    if (peopleInSameCategory.length === 1) {
      // If only one person in the category, just hide them and clear their name
      const updatedPeople = people.map(p =>
        p.id === personId
          ? { ...p, isVisible: false }
          : p
      );

      // Clear the person's name
      const updatedPersonNames = { ...personNames };
      delete updatedPersonNames[personId];

      // Remove person's assignments from all tasks
      const updatedTasks = tasks.map(task => {
        const newAssignments = { ...task.assignments };
        delete newAssignments[personId];
        return { ...task, assignments: newAssignments };
      });

      setPeople(updatedPeople);
      setPersonNames(updatedPersonNames);
      onTasksChange(updatedTasks);

      if (onPeopleConfigChange) {
        onPeopleConfigChange(updatedPeople, updatedPersonNames);
      }

      showSnackbar(`Hidden ${personNames[personId] || personId} (last person in ${personToRemove.category})`, 'info');
    } else {
      // If multiple people in the category, fully remove the person
      const updatedPeople = people.filter(p => p.id !== personId);

      // Remove person from personNames
      const updatedPersonNames = { ...personNames };
      delete updatedPersonNames[personId];

      // Remove person's assignments from all tasks
      const updatedTasks = tasks.map(task => {
        const newAssignments = { ...task.assignments };
        delete newAssignments[personId];
        return { ...task, assignments: newAssignments };
      });

      setPeople(updatedPeople);
      setPersonNames(updatedPersonNames);
      onTasksChange(updatedTasks);

      if (onPeopleConfigChange) {
        onPeopleConfigChange(updatedPeople, updatedPersonNames);
      }

      showSnackbar(`Removed ${personNames[personId] || personId} from the matrix`, 'success');
    }
  };

  const handleHideUnassignedPeople = () => {
    const assignedPeopleIds = new Set<string>();
    tasks.forEach(task => {
      Object.keys(task.assignments).forEach(personId => {
        assignedPeopleIds.add(personId);
      });
    });
    
    const newPeople = people.map(p => ({ 
      ...p, 
      isVisible: assignedPeopleIds.has(p.id) 
    }));
    setPeople(newPeople);
    if (onPeopleConfigChange) {
      onPeopleConfigChange(newPeople, personNames);
    }
    showSnackbar('Unassigned team members hidden', 'success');
  };

  // Helper function to get task number
  const getTaskNumber = (taskId: string): number => {
    return tasks.findIndex(t => t.id === taskId) + 1;
  };

  // Validation functions
  const validateMatrix = (): string[] => {
    const errors: string[] = [];
    
    // Check for empty tasks
    const emptyTasks = tasks.filter(task => !task.workArea.trim() || !task.taskDescription.trim());
    if (emptyTasks.length > 0) {
      const taskNumbers = emptyTasks.map(task => getTaskNumber(task.id)).sort((a, b) => a - b);
      errors.push(`Task${taskNumbers.length > 1 ? 's' : ''} ${taskNumbers.join(', ')} ${taskNumbers.length > 1 ? 'have' : 'has'} empty work area or description`);
    }

    // Check for tasks without any assignments
    const unassignedTasks = tasks.filter(task => Object.keys(task.assignments).length === 0);
    if (unassignedTasks.length > 0) {
      const taskNumbers = unassignedTasks.map(task => getTaskNumber(task.id)).sort((a, b) => a - b);
      errors.push(`Task${taskNumbers.length > 1 ? 's' : ''} ${taskNumbers.join(', ')} ${taskNumbers.length > 1 ? 'have' : 'has'} no role assignments`);
    }

    // Check for tasks without 'D' (Decide) role
    const tasksWithoutDecider = tasks.filter(task => 
      !Object.values(task.assignments).some(role => role.includes('D'))
    );
    if (tasksWithoutDecider.length > 0) {
      const taskNumbers = tasksWithoutDecider.map(task => getTaskNumber(task.id)).sort((a, b) => a - b);
      errors.push(`Task${taskNumbers.length > 1 ? 's' : ''} ${taskNumbers.join(', ')} missing a 'Decide' role assignment`);
    }

    // Check for tasks without 'R' (Recommend) role
    const tasksWithoutRecommender = tasks.filter(task => 
      !Object.values(task.assignments).some(role => role.includes('R'))
    );
    if (tasksWithoutRecommender.length > 0) {
      const taskNumbers = tasksWithoutRecommender.map(task => getTaskNumber(task.id)).sort((a, b) => a - b);
      errors.push(`Task${taskNumbers.length > 1 ? 's' : ''} ${taskNumbers.join(', ')} missing a 'Recommend' role assignment`);
    }

    return errors;
  };

  // Export functions
  const handleExport = async (format: 'excel' | 'csv' | 'pdf' | 'json') => {
    const errors = validateMatrix();
    if (errors.length > 0 && format !== 'json') {
      setValidationErrors(errors);
      showSnackbar('Please fix validation errors before exporting', 'error');
      return;
    }

    setExportLoading(true);
    try {
      const visiblePeople = people.filter(p => p.isVisible !== false);
      const exportData: ExportData = {
        projectInfo: { date: new Date().toLocaleDateString(), projectSummary: projectName },
        tasks,
        people: visiblePeople.map(p => ({ id: p.id, name: personNames[p.id] || p.id, category: p.category })),
        personNames,
        matrixType: 'RAPID'
      };

      switch (format) {
        case 'excel':
          exportToExcel(exportData, projectName);
          showSnackbar('Excel file exported successfully', 'success');
          break;
        case 'csv':
          exportToCSV(exportData, projectName);
          showSnackbar('CSV file exported successfully', 'success');
          break;
        case 'pdf':
          if (matrixRef.current) {
            await exportToPDF('matrix-container', projectName, 'RAPID');
            showSnackbar('PDF file exported successfully', 'success');
          }
          break;
        case 'json':
          exportToJSON(exportData, projectName);
          showSnackbar('JSON file exported successfully', 'success');
          break;
      }
    } catch (error) {
      console.error('Export error:', error);
      showSnackbar('Export failed. Please try again.', 'error');
    } finally {
      setExportLoading(false);
      setExportDialog(false);
    }
  };

  const handleCloneTask = (taskId: string) => {
    const taskToClone = tasks.find(t => t.id === taskId);
    if (taskToClone) {
      const clonedTask: Task = {
        ...taskToClone,
        id: `task-${Date.now()}`,
        workArea: `${taskToClone.workArea} (Copy)`,
        taskDescription: `${taskToClone.taskDescription} (Copy)`
      };
      onTasksChange([...tasks, clonedTask]);
      showSnackbar('Task cloned successfully', 'success');
    }
  };

  // Filter tasks based on search term
  const filteredTasks = tasks.filter(task =>
    task.workArea.toLowerCase().includes(searchTerm.toLowerCase()) ||
    task.taskDescription.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getCategoryHeader = (category: string) => {
    const peopleInCategory = people.filter(p => p.category === category && p.isVisible !== false);
    const allInCategory = people.filter(p => p.category === category);
    const allVisible = allInCategory.every(p => p.isVisible !== false);
    const categoryColors = {
      'Leadership Team': '#1a5f7a', // Dark teal
      'Project Team': '#2d7a8a',    // Medium teal
      'External Resources': '#4a9ba8' // Light teal
    };
    
    if (peopleInCategory.length === 0) {
      return null; // Don't render header if no visible people
    }
    
    return (
      <TableCell 
        colSpan={peopleInCategory.length} 
        sx={{ 
          backgroundColor: categoryColors[category as keyof typeof categoryColors] || '#1a5f7a', 
          color: 'white',
          textAlign: 'center',
          fontWeight: 'bold',
          position: 'relative'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography>{category} ({peopleInCategory.length}/{allInCategory.length})</Typography>
          <Tooltip title={allVisible ? `Hide all ${category}` : `Show all ${category}`} placement="top">
            <IconButton
              size="small"
              onClick={() => handleToggleCategoryVisibility(category, !allVisible)}
              sx={{ color: 'white', ml: 1 }}
            >
              {allVisible ? <VisibilityOffIcon /> : <VisibilityIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
    );
  };

  const getPersonCellStyle = (person: any) => {
    const categoryColors = {
      'Leadership Team': '#1a5f7a', // Dark teal
      'Project Team': '#2d7a8a',    // Medium teal
      'External Resources': '#4a9ba8' // Light teal
    };
    
    return {
      backgroundColor: categoryColors[person.category as keyof typeof categoryColors] || '#1a5f7a',
      color: 'white',
      fontWeight: 'bold',
      textAlign: 'center' as const,
      minWidth: 60,
      maxWidth: 60,
      width: 60,
      borderRight: '1px solid #ddd',
      position: 'relative'
    };
  };

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
          Decision-Making Matrix
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddTask}
            sx={{ backgroundColor: '#27ae60' }}
          >
            Add Task
          </Button>
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => setAddPersonDialog(true)}
            sx={{ backgroundColor: '#2980b9' }}
          >
            Add Person
          </Button>
          <Button
            variant="outlined"
            startIcon={<VisibilityIcon />}
            onClick={handleShowAllPeople}
            size="small"
          >
            Show All
          </Button>
          <Button
            variant="outlined"
            startIcon={<VisibilityOffIcon />}
            onClick={handleHideUnassignedPeople}
            size="small"
          >
            Hide Unassigned
          </Button>
          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            onClick={() => setExportDialog(true)}
            sx={{ borderColor: '#3498db', color: '#3498db' }}
          >
            Export
          </Button>
        </Box>
      </Box>

      {/* Search and Filter Bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
        <TextField
          size="small"
          placeholder="Search tasks..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ color: '#666', mr: 1 }} />,
            endAdornment: searchTerm && (
              <IconButton size="small" onClick={() => setSearchTerm('')}>
                <ClearIcon />
              </IconButton>
            )
          }}
          sx={{ minWidth: 300 }}
        />
        {tasks.length > 0 && (
          <Typography variant="body2" color="text.secondary">
            {filteredTasks.length} of {tasks.length} tasks
          </Typography>
        )}
      </Box>

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setValidationErrors([])}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Matrix Validation Issues:</Typography>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {validationErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      <div id="matrix-container" ref={matrixRef}>
        <TableContainer>
          <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ backgroundColor: 'transparent', border: 'none', padding: 0 }}></TableCell>
              <TableCell sx={{ backgroundColor: 'transparent', border: 'none', padding: 0 }}></TableCell>
              {getCategoryHeader('Leadership Team')}
              {getCategoryHeader('Project Team')}
              {getCategoryHeader('External Resources')}
              <TableCell sx={{ backgroundColor: 'transparent', border: 'none', padding: 0 }}></TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ 
                backgroundColor: '#0d4a5a', // Dark teal for Work Area
                color: 'white', 
                fontWeight: 'bold',
                minWidth: 200,
                width: '25%',
                borderRight: '1px solid #ddd'
              }}>
                Work Area
              </TableCell>
              <TableCell sx={{ 
                backgroundColor: '#0d4a5a', // Dark teal for Task Description
                color: 'white', 
                fontWeight: 'bold',
                minWidth: 300,
                width: '35%',
                borderRight: '1px solid #ddd'
              }}>
                Task Description
              </TableCell>
              {people.filter(p => p.isVisible !== false).map((person) => (
                <TableCell 
                  key={person.id}
                  sx={getPersonCellStyle(person)}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                      {person.id}
                    </Typography>
                    <TextField
                      size="small"
                      value={personNames[person.id] || ''}
                      onChange={(e) => handlePersonNameChange(person.id, e.target.value)}
                      placeholder="Name"
                      variant="standard"
                      sx={{
                        '& .MuiInput-root': {
                          color: 'white',
                          fontSize: '0.6rem',
                          minHeight: '20px'
                        },
                        '& .MuiInput-root::before': {
                          borderBottom: '1px solid rgba(255,255,255,0.3)'
                        },
                        '& .MuiInput-root:hover::before': {
                          borderBottom: '1px solid rgba(255,255,255,0.5)'
                        },
                        '& .MuiInput-input': {
                          padding: '2px 0',
                          textAlign: 'center'
                        }
                      }}
                    />
                    <Tooltip title={person.isVisible !== false ? "Hide person" : "Show person"} placement="bottom">
                      <IconButton
                        size="small"
                        onClick={() => handleTogglePersonVisibility(person.id)}
                        sx={{ color: 'white', p: 0, minWidth: 16, height: 16, mt: 0.5 }}
                      >
                        {person.isVisible !== false ?
                          <VisibilityOffIcon sx={{ fontSize: '0.7rem' }} /> :
                          <VisibilityIcon sx={{ fontSize: '0.7rem' }} />
                        }
                      </IconButton>
                    </Tooltip>
                    <Tooltip
                      title={
                        people.filter(p => p.category === person.category).length === 1
                          ? "Hide person (last in group)"
                          : "Remove person from matrix"
                      }
                      placement="bottom"
                    >
                      <IconButton
                        size="small"
                        onClick={() => handleRemovePerson(person.id)}
                        sx={{ color: 'white', p: 0, minWidth: 16, height: 16, mt: 0.5 }}
                      >
                        <PersonRemoveIcon sx={{ fontSize: '0.7rem' }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              ))}
              <TableCell sx={{ 
                backgroundColor: '#0d4a5a', // Dark teal for Actions
                color: 'white', 
                fontWeight: 'bold', 
                width: 60 
              }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTasks.map((task, index) => (
              <TableRow key={task.id} sx={{ '&:nth-of-type(odd)': { backgroundColor: 'action.hover' } }}>
                <TableCell sx={{ minWidth: 200, width: '25%', borderRight: '1px solid #ddd' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ 
                      fontWeight: 'bold', 
                      color: 'text.primary',
                      minWidth: '20px',
                      textAlign: 'center'
                    }}>
                      {tasks.findIndex(t => t.id === task.id) + 1}.
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      value={task.workArea}
                      onChange={(e) => handleTaskChange(task.id, 'workArea', e.target.value)}
                      placeholder="Enter work area"
                      variant="standard"
                    />
                  </Box>
                </TableCell>
                <TableCell sx={{ minWidth: 300, width: '35%', borderRight: '1px solid #ddd' }}>
                  <TextField
                    fullWidth
                    size="small"
                    value={task.taskDescription}
                    onChange={(e) => handleTaskChange(task.id, 'taskDescription', e.target.value)}
                    placeholder="Enter task description"
                    variant="standard"
                  />
                </TableCell>
                {people.filter(p => p.isVisible !== false).map((person) => {
                  const assignedRole = task.assignments[person.id] || '';
                  return (
                    <TableCell 
                      key={person.id}
                      sx={{ 
                        textAlign: 'center',
                        cursor: 'pointer',
                        backgroundColor: assignedRole ? getRoleColor(assignedRole) : 'transparent',
                        color: assignedRole ? 'white' : 'inherit',
                        fontWeight: assignedRole ? 'bold' : 'normal',
                        minWidth: 60,
                        maxWidth: 60,
                        width: 60,
                        borderRight: '1px solid #ddd',
                        '&:hover': {
                          backgroundColor: assignedRole ? getRoleColor(assignedRole) : '#e3f2fd',
                          opacity: 0.8
                        }
                      }}
                      onClick={(e) => handleCellClick(e, task.id, person.id)}
                    >
                      {assignedRole ? (
                        <Chip
                          label={assignedRole}
                          size="small"
                          sx={{ 
                            backgroundColor: 'rgba(255,255,255,0.2)',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '0.7rem',
                            height: 20
                          }}
                        />
                      ) : (
                        <AddIcon sx={{ color: '#ccc', fontSize: '1.2rem' }} />
                      )}
                    </TableCell>
                  );
                })}
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Clone task">
                      <IconButton
                        size="small"
                        onClick={() => handleCloneTask(task.id)}
                        sx={{ color: '#3498db' }}
                      >
                        <CloneIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete task">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteTask(task.id)}
                        sx={{ color: '#e74c3c' }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </TableContainer>
      </div>

      {/* Role Assignment Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        <MenuItem onClick={() => handleRoleAssignment(selectedCell?.taskId || '', selectedCell?.personId || '', '')}>
          <Typography color="text.secondary">Clear Assignment</Typography>
        </MenuItem>
        <Divider />
        {RAPID_ROLES.map((role) => (
          <MenuItem 
            key={role.id}
            onClick={() => handleRoleAssignment(selectedCell?.taskId || '', selectedCell?.personId || '', role.id)}
            sx={{ 
              backgroundColor: role.color,
              color: 'white',
              '&:hover': {
                backgroundColor: role.color,
                opacity: 0.8
              }
            }}
          >
            <Typography sx={{ fontWeight: 'bold' }}>
              {role.id} - {role.name}
            </Typography>
          </MenuItem>
        ))}
      </Menu>

      {/* Add Person Dialog */}
      <Dialog open={addPersonDialog} onClose={() => setAddPersonDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Person</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Add a new person to the RAPID matrix and assign them to a group.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              fullWidth
              label="Person Name"
              value={newPersonName}
              onChange={(e) => setNewPersonName(e.target.value)}
              placeholder="Enter person's name"
              autoFocus
            />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Select Group:</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {['Leadership Team', 'Project Team', 'External Resources'].map((category) => (
                  <Button
                    key={category}
                    variant={newPersonCategory === category ? 'contained' : 'outlined'}
                    onClick={() => setNewPersonCategory(category as typeof newPersonCategory)}
                    sx={{
                      justifyContent: 'flex-start',
                      textTransform: 'none',
                      backgroundColor: newPersonCategory === category ?
                        (category === 'Leadership Team' ? '#1a5f7a' :
                         category === 'Project Team' ? '#2d7a8a' : '#4a9ba8') : 'transparent'
                    }}
                    startIcon={<GroupIcon />}
                  >
                    {category}
                  </Button>
                ))}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddPersonDialog(false)}>Cancel</Button>
          <Button
            onClick={handleAddPerson}
            variant="contained"
            disabled={!newPersonName.trim()}
          >
            Add Person
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export Dialog */}
      <Dialog open={exportDialog} onClose={() => setExportDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Export RAPID Matrix</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Choose a format to export your RAPID Decision Matrix. The matrix will be validated before export.
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => handleExport('excel')}
              disabled={exportLoading}
              sx={{ justifyContent: 'flex-start', p: 2 }}
            >
              <Box sx={{ textAlign: 'left' }}>
                <Typography variant="subtitle2">Excel (.xlsx)</Typography>
                <Typography variant="caption" color="text.secondary">
                  Full-featured spreadsheet with multiple sheets and formatting
                </Typography>
              </Box>
            </Button>
            
            <Button
              variant="outlined"
              fullWidth
              onClick={() => handleExport('csv')}
              disabled={exportLoading}
              sx={{ justifyContent: 'flex-start', p: 2 }}
            >
              <Box sx={{ textAlign: 'left' }}>
                <Typography variant="subtitle2">CSV (.csv)</Typography>
                <Typography variant="caption" color="text.secondary">
                  Simple comma-separated values format
                </Typography>
              </Box>
            </Button>
            
            <Button
              variant="outlined"
              fullWidth
              onClick={() => handleExport('pdf')}
              disabled={exportLoading}
              sx={{ justifyContent: 'flex-start', p: 2 }}
            >
              <Box sx={{ textAlign: 'left' }}>
                <Typography variant="subtitle2">PDF (.pdf)</Typography>
                <Typography variant="caption" color="text.secondary">
                  Print-ready document with visual formatting
                </Typography>
              </Box>
            </Button>
            
            <Button
              variant="outlined"
              fullWidth
              onClick={() => handleExport('json')}
              disabled={exportLoading}
              sx={{ justifyContent: 'flex-start', p: 2 }}
            >
              <Box sx={{ textAlign: 'left' }}>
                <Typography variant="subtitle2">JSON (.json)</Typography>
                <Typography variant="caption" color="text.secondary">
                  Raw data format for backup or import into other systems
                </Typography>
              </Box>
            </Button>
          </Box>
          
          {exportLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 2 }}>
              <CircularProgress size={24} sx={{ mr: 2 }} />
              <Typography variant="body2">Generating export...</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialog(false)} disabled={exportLoading}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default DecisionMatrix; 